import type { MyGoogleUserData } from '../interfaces/MyGoogleUserData';
import { ApiService } from '../utility-services/Api.service';
import type { UserAccess } from '../interfaces/UserAccess';
import type { SearchMyGoogleUser } from '../interfaces/SearchUser';
import type { UserRights } from '../interfaces/UserRights';

export class UserService {
	private static userRightsPromise?: Promise<UserRights[]>;

	apiService = new ApiService();
	newApiService = new ApiService(
		import.meta.env.VITE_API_BASE_PATH,
		import.meta.env.VITE_X_API_KEY
	);

	getUser(email: string): Promise<MyGoogleUserData> {
		return this.apiService
			.get<MyGoogleUserData>(`users/${email}`)
			.then((data: MyGoogleUserData) => data);
	}

	getUsersByEmails(emails: string[]): Promise<MyGoogleUserData[]> {
		if (!emails || emails.length === 0) return Promise.resolve([]);

		const uniqueEmails = Array.from(new Set(emails.filter(Boolean).map((e) => e.toLowerCase())));
		return this.apiService
			.post<MyGoogleUserData[], string[]>(`users/search`, uniqueEmails)
			.then((data: MyGoogleUserData[]) => data);
	}

	searchUser(query: string, organizationalUnit: string, pageToken: string, pageSize?: number) {
		return this.apiService
			.get<SearchMyGoogleUser>(`users/generics`, undefined, {
				organizational_unit: organizationalUnit,
				query,
				pageToken,
				...(pageSize === undefined ? {} : { pageSize })
			})
			.then((data: { myGoogleUserDatasList: MyGoogleUserData[]; pageToken: string }) => data);
	}

	checkUserEditAccess(email: string): Promise<UserAccess> {
		return this.apiService
			.get<UserAccess>(`users/${email}/access`)
			.then((data: UserAccess) => data);
	}

	checkUsersPermissions(emails: string[]): Promise<UserAccess[]> {
		return this.apiService
			.post<UserAccess[], string[]>(`users/check_user_permissions`, emails)
			.then((data: UserAccess[]) => data);
	}

	getUserRights(): Promise<UserRights[]> {
		UserService.userRightsPromise ??= this.newApiService
			.get<UserRights[]>(`users/me/rights`)
			.then((data: UserRights[]) => data)
			.catch((error) => {
				UserService.userRightsPromise = undefined;
				throw error;
			});

		return UserService.userRightsPromise;
	}

	static clearUserRightsCache(): void {
		UserService.userRightsPromise = undefined;
	}
}
